import React, { useState, useRef } from 'react';
import { Receipt, Upload, FileText, Send, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { puntovService } from '../../../services/api';
import Modal from './Modal';
import { fmtUsd, fmtFecha, fmtFechaHora, fmtPeriodo, textoPlazoFactura } from './formato';
import { abrirPdf } from './abrirPdf';

const claseInput = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none';

const ALERTA = {
  vencida: { clase: 'bg-red-50 border-red-200 text-red-700', icono: AlertTriangle },
  pendiente: { clase: 'bg-amber-50 border-amber-200 text-amber-800', icono: Clock },
  enviada: { clase: 'bg-green-50 border-green-200 text-green-700', icono: CheckCircle2 }
};

// Factura de la mensualidad que el cliente solicitó en Punto V: se adjunta
// el PDF y se le envía por correo.
const DialogoFactura = ({ solicitud, onClose, onCambio }) => {
  const [s, setS] = useState(solicitud);
  const [archivo, setArchivo] = useState(null);
  const [facturaNumero, setFacturaNumero] = useState(solicitud.factura_numero || '');
  const [correo, setCorreo] = useState(solicitud.correo_destino || '');
  const [trabajando, setTrabajando] = useState(null);
  const [error, setError] = useState('');
  const [resultado, setResultado] = useState(null);
  const inputArchivo = useRef(null);

  const alerta = ALERTA[s.situacion] || ALERTA.pendiente;
  const IconoAlerta = alerta.icono;

  const ver = (descargar) => {
    setError('');
    abrirPdf(descargar).catch(err => setError(err.message));
  };
  const verFactura = () => ver(() => puntovService.descargarFactura(s.id));
  const verCotizacion = () => ver(() => puntovService.verCotizacionPdf(s.cotizacion_id));

  const elegirArchivo = (e) => {
    const f = e.target.files?.[0] || null;
    setError('');
    if (f && f.type !== 'application/pdf') {
      setError('La factura debe ser un archivo PDF');
      e.target.value = '';
      return;
    }
    setArchivo(f);
  };

  // Adjunta (si eligió un archivo nuevo) y envía. Si el envío falla, la
  // factura queda adjunta y se puede reintentar.
  const adjuntarYEnviar = async (enviar) => {
    setError('');
    setResultado(null);
    try {
      let actual = s;
      if (archivo) {
        setTrabajando('adjuntando');
        const res = await puntovService.adjuntarFactura(s.id, archivo, facturaNumero.trim());
        actual = { ...s, ...res.data };
        setS(actual);
        setArchivo(null);
        if (inputArchivo.current) inputArchivo.current.value = '';
        onCambio();
      }
      if (!enviar) {
        setResultado({ tipo: 'exito', texto: 'Factura adjuntada. Aún no se ha enviado al cliente.' });
        return;
      }
      setTrabajando('enviando');
      const res = await puntovService.enviarFactura(s.id, correo.trim());
      setS({ ...actual, situacion: 'enviada', enviado_at: new Date().toISOString(), correo_enviado_a: res.data.para, error_envio: null });
      setResultado({ tipo: 'exito', texto: res.message });
      onCambio();
    } catch (err) {
      setResultado({ tipo: 'error', texto: err.message || 'No se pudo completar la operación' });
      onCambio();
    } finally {
      setTrabajando(null);
    }
  };

  const tieneFactura = Boolean(s.factura_archivo) || Boolean(archivo);

  return (
    <Modal
      titulo={`Factura de ${fmtPeriodo(s.periodo)}`}
      subtitulo={`${s.numero} · ${s.cliente || s.empresa}`}
      icono={Receipt}
      onClose={onClose}
      bloqueado={Boolean(trabajando)}
    >
      <div className="space-y-5">
        <div className={`rounded-xl border p-3 text-sm flex items-start gap-2 ${alerta.clase}`}>
          <IconoAlerta size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            {s.situacion === 'enviada' ? (
              <>Enviada a <strong>{s.correo_enviado_a}</strong> el {fmtFechaHora(s.enviado_at)}.</>
            ) : (
              <>
                <strong>{textoPlazoFactura(s)}</strong> para entregarla. El cliente la pidió el {fmtFechaHora(s.solicitada_at)};
                {' '}la fecha límite es el {fmtFecha(s.fecha_limite)} (5 días hábiles).
              </>
            )}
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-gray-500">Cotización</dt>
          <dd className="font-medium text-gray-800 flex items-center gap-2">
            {s.cotizacion_numero || '—'}
            {s.cotizacion_id && (
              <button type="button" onClick={verCotizacion} className="text-xs font-normal text-blue-600 hover:text-blue-800">
                Ver cotización
              </button>
            )}
          </dd>
          <dt className="text-gray-500">Mensualidad</dt>
          <dd className="text-gray-800">{fmtPeriodo(s.periodo)}</dd>
          <dt className="text-gray-500">Monto (sin IVA)</dt>
          <dd className="text-gray-800 tabular-nums">{fmtUsd(s.total_usd)}</dd>
        </dl>

        <section className="space-y-3 border-t border-gray-100 pt-4">
          <h4 className="text-sm font-semibold text-gray-800">Factura</h4>
          {s.factura_archivo && (
            <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl text-sm">
              <FileText size={18} className="text-red-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-800 truncate">{s.factura_nombre}</div>
                <div className="text-xs text-gray-500">
                  {s.factura_numero ? `N.º ${s.factura_numero} · ` : ''}adjuntada {fmtFechaHora(s.factura_subida_at)}
                </div>
              </div>
              <button type="button" onClick={verFactura} className="text-sm text-blue-600 hover:text-blue-800">Ver factura</button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="factura-archivo" className="block text-xs font-medium text-gray-700 mb-1">
                {s.factura_archivo ? 'Reemplazar PDF' : 'PDF de la factura *'}
              </label>
              <label
                htmlFor="factura-archivo"
                className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 cursor-pointer hover:border-blue-400 hover:bg-blue-50/40"
              >
                <Upload size={15} className="flex-shrink-0" />
                <span className="truncate">{archivo ? archivo.name : 'Elegir archivo PDF'}</span>
              </label>
              <input
                id="factura-archivo"
                ref={inputArchivo}
                type="file"
                accept="application/pdf,.pdf"
                onChange={elegirArchivo}
                className="sr-only"
              />
            </div>
            <div>
              <label htmlFor="factura-numero" className="block text-xs font-medium text-gray-700 mb-1">
                N.º de factura <span className="font-normal text-gray-400">(opcional)</span>
              </label>
              <input
                id="factura-numero"
                value={facturaNumero}
                onChange={(e) => setFacturaNumero(e.target.value)}
                maxLength={50}
                placeholder="00012345"
                className={`${claseInput} tabular-nums`}
              />
            </div>
          </div>

          <div>
            <label htmlFor="factura-correo" className="block text-xs font-medium text-gray-700 mb-1">Enviar a</label>
            <input
              id="factura-correo"
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              placeholder="Correo del cliente"
              className={claseInput}
            />
          </div>
        </section>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> {error}
          </div>
        )}
        {resultado && (
          <div className={`rounded-lg p-3 text-sm border flex items-start gap-2 ${
            resultado.tipo === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
          }`}>
            {resultado.tipo === 'error' ? <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> : <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />}
            {resultado.texto}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onClose} disabled={Boolean(trabajando)} className="px-4 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-100">
            Cerrar
          </button>
          {archivo && (
            <button
              type="button"
              onClick={() => adjuntarYEnviar(false)}
              disabled={Boolean(trabajando)}
              className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Solo adjuntar
            </button>
          )}
          <button
            type="button"
            onClick={() => adjuntarYEnviar(true)}
            disabled={Boolean(trabajando) || !tieneFactura || !correo.trim()}
            title={!tieneFactura ? 'Adjunte primero el PDF de la factura' : undefined}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            <Send size={15} />
            {trabajando === 'adjuntando' ? 'Adjuntando…'
              : trabajando === 'enviando' ? 'Enviando…'
                : archivo ? 'Adjuntar y enviar'
                  : s.situacion === 'enviada' ? 'Reenviar factura' : 'Enviar factura'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DialogoFactura;
